import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import NavigationHeader from '../../components/ui/NavigationHeader';
import SearchResultsHeader from './components/SearchResultsHeader';
import SearchResultsGrid from './components/SearchResultsGrid';
import NoResultsState from './components/NoResultsState';
import { clientApiService } from '../../utils/clientApiService';
import { transactionApiService } from '../../utils/transactionApiService';

const SearchResults = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [clients, setClients] = useState([]);

  // Only use the URL param for search query
  const searchQuery = new URLSearchParams(location.search).get('q') || '';

  useEffect(() => {
    if (searchQuery) {
      setIsLoading(true);
      const fetchClients = async () => {
        try {
          const filtered = await clientApiService.searchClients(searchQuery);
          setClients(filtered);
        } catch (error) {
          setClients([]);
        } finally {
          setIsLoading(false);
        }
      };
      // Simulate search delay for better UX
      const timer = setTimeout(fetchClients, 300);
      return () => clearTimeout(timer);
    } else {
      setClients([]);
      setIsLoading(false);
    }
  }, [searchQuery]);


  // Helper to get current username from JWT
  const getCurrentUsername = () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return 'Unknown';
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.username || 'Unknown';
    } catch {
      return 'Unknown';
    }
  };

  // Handle adding minutes to client (with transaction)
  const handleAddMinutes = async (clientId, minutes) => {
    try {
      const client = clients.find(c => c.id === clientId);
      if (!client) throw new Error('Client not found');
      await transactionApiService.addTransaction({
        clientId,
        type: 'add',
        amount: Number(minutes),
        description: `Quick add ${minutes} minutes`,
        staff: getCurrentUsername()
      });
      // Refresh client data
      const updatedClient = await clientApiService.getClientById(clientId);
      setClients(prevClients => prevClients.map(client => client.id === clientId ? updatedClient : client));
      console.log(`Added ${minutes} minutes to client ${clientId}`);
    } catch (error) {
      console.error('Error adding minutes:', error);
      alert(error.message || 'Failed to add minutes. Please try again.');
    }
  };

  // Handle subtracting minutes from client (with transaction)
  const handleSubtractMinutes = async (clientId, minutes) => {
    try {
      const client = clients.find(c => c.id === clientId);
      if (!client) throw new Error('Client not found');
      if (client.balance < minutes) throw new Error('Insufficient balance for this transaction');
      await transactionApiService.addTransaction({
        clientId,
        type: 'subtract',
        amount: Number(minutes),
        description: `Quick subtract ${minutes} minutes`,
        staff: getCurrentUsername()
      });
      // Refresh client data
      const updatedClient = await clientApiService.getClientById(clientId);
      setClients(prevClients => prevClients.map(client => client.id === clientId ? updatedClient : client));
      console.log(`Subtracted ${minutes} minutes from client ${clientId}`);
    } catch (error) {
      console.error('Error subtracting minutes:', error);
      alert(error.message || 'Failed to subtract minutes. Please try again.');
    }
  };

  const handleClearSearch = () => {
    navigate('/client-dashboard');
  };

  const handleAddNewClient = () => {
    navigate('/add-new-client');
  };

  return (
    <div className="min-h-screen bg-background">
      <NavigationHeader />
      <main className="pt-16">
        <SearchResultsHeader
          searchQuery={searchQuery}
          resultsCount={clients.length}
          onClearSearch={handleClearSearch}
          isLoading={isLoading}
        />
        {/* Results Content */}
        {!searchQuery ? (
          <div className="flex flex-col items-center justify-center py-24">
            <h2 className="text-2xl font-semibold text-muted-foreground mb-2">Start typing to search for clients</h2>
            <p className="text-muted-foreground">Use the search bar above to find clients by name or phone number.</p>
          </div>
        ) : !isLoading && clients.length === 0 ? (
          <NoResultsState
            searchQuery={searchQuery}
            onClearSearch={handleClearSearch}
            onAddNewClient={handleAddNewClient}
          />
        ) : (
          <SearchResultsGrid
            clients={clients}
            searchQuery={searchQuery}
            onAddMinutes={handleAddMinutes}
            onSubtractMinutes={handleSubtractMinutes}
            isLoading={isLoading}
          />
        )}
        {/* Results Summary Footer */}
        {!isLoading && clients.length > 0 && (
          <div className="px-6 py-4 border-t border-border bg-muted/20">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="text-sm text-muted-foreground">
                Showing {clients.length} result{clients.length !== 1 ? 's' : ''} for "{searchQuery}"
              </div>
              <div className="text-xs text-muted-foreground">
                Last updated: {new Date().toLocaleTimeString()}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default SearchResults;